import moment, { MomentInput } from 'moment';
import { ProjectPhase } from 'api/project';
import { Task } from 'api/tasks';
import { formatDate } from 'data/projects/gantt/dates';
import { extractParts } from 'data/projects/phase-entity';
import {
  ProjectTemplateProjectPhase,
  ProjectTemplateSprint,
} from 'data/templates/projects';
import { TemplateTask } from 'data/templates/projects/sagas/task/rearrange';
import {
  DateShiftUpdates,
  DateShiftUpdatesForPhase,
  DateShiftUpdatesForSprint,
  DateShiftUpdatesForTask,
  DependencyTypesEnum,
  DependencyTypesEnumReverse,
  Entity,
  EntityDateShiftUpdate,
  EntityType,
  List,
} from './date-shift';

type TemplateEntity = Entity & {
  duration: number;
};

type TemplateList = {
  [id: number]: TemplateEntity;
};

export const getPhaseList = (
  phases: ProjectTemplateProjectPhase[],
): TemplateList => {
  const result: TemplateList = {};

  phases.forEach(
    ({
      projectPhaseId,
      projectPhaseName,
      startDay,
      duration,
      entityOrder,
      dependentProjectPhases,
    }) => {
      result[projectPhaseId] = {
        depth: 0,
        id: projectPhaseId,
        name: projectPhaseName,
        type: EntityType.Phase,
        startDate: formatDate(moment().add(startDay, 'day')),
        dueDate: formatDate(moment().add(startDay + duration - 1, 'day')),
        duration,
        children:
          entityOrder?.map((entityId) => extractParts(entityId).id) ?? [],
        dependentOn: dependentProjectPhases?.map(
          ({ projectPhaseId: dPhaseId, dependencyType, lag }) => {
            return {
              id: dPhaseId,
              dependencyType: dependencyType
                ? DependencyTypesEnum[dependencyType]
                : 2,
              lag: lag ?? null,
            };
          },
        ),
      };

      dependentProjectPhases?.forEach(
        ({ projectPhaseId: dPhaseId, dependencyType, lag }) => {
          result[dPhaseId] = {
            ...(result[dPhaseId] ?? {}),
            dependencyFor: [
              ...(result[dPhaseId]?.dependencyFor ?? []),
              // projectPhaseId,
              {
                id: projectPhaseId,
                dependencyType: dependencyType
                  ? DependencyTypesEnum[dependencyType]
                  : 2,
                lag: lag ?? null,
              },
            ],
          };
        },
      );
    },
  );

  return result;
};

export const getTaskList = (tasks: TemplateTask[]): TemplateList => {
  const result: TemplateList = {};

  tasks
    .filter(({ isNew }) => !isNew)
    .forEach(
      ({
        taskId,
        startDay,
        duration,
        subTasks,
        dependentTasks,
        parentTask,
        projectPhase,
        taskName,
      }) => {
        result[taskId] = {
          ...(result[taskId] ?? {}),
          depth: 1, // initial value
          name: taskName,
          id: taskId,
          type: EntityType.Task,
          startDate: formatDate(moment().add(startDay, 'day')),
          dueDate: formatDate(moment().add(startDay + duration - 1, 'day')),
          duration,
          children: subTasks?.map((_task) => _task?.taskId ?? _task),
          parent: parentTask?.taskId ?? projectPhase?.projectPhaseId,
        };

        dependentTasks?.forEach(({ taskId: dTaskId, dependencyType, lag }) => {
          if (dependencyType !== undefined && dependencyType !== null) {
            result[dTaskId] = {
              ...(result[dTaskId] ?? {}),
              dependencyFor: [
                ...(result[dTaskId]?.dependencyFor ?? []),
                {
                  id: taskId,
                  dependencyType: DependencyTypesEnum[dependencyType],
                  lag: lag ?? null,
                },
              ],
            };
            result[taskId] = {
              ...(result[taskId] ?? {}),
              dependentOn: [
                ...(result[taskId]?.dependentOn ?? []),
                {
                  id: dTaskId,
                  dependencyType: DependencyTypesEnum[dependencyType],
                  lag: lag ?? null,
                },
              ],
            };
          }
        });
      },
    );

  const getDepth = (id) => {
    return !result[id]?.parent ? 0 : getDepth(result[id].parent) + 1;
  };

  Object.values(result).forEach((_task) => {
    result[_task.id] = {
      ..._task,
      depth: getDepth(_task.id),
    };
  });

  return result;
};

export const getSprintList = (
  sprints: ProjectTemplateSprint[],
): TemplateList => {
  const result: TemplateList = {};

  sprints
    .filter(({ isNew }) => !isNew)
    .forEach(({ sprintId, startDay, duration, projectPhase, sprintName }) => {
      result[sprintId] = {
        ...(result[sprintId] ?? {}),
        depth: 1, // initial value
        name: sprintName,
        id: sprintId,
        type: EntityType.Sprint,
        startDate: formatDate(moment().add(startDay, 'day')),
        dueDate: formatDate(moment().add(startDay + duration - 1, 'day')),
        duration,
        children: [],
        dependentOn: [],
        parent: projectPhase?.projectPhaseId,
      };
    });

  const getDepth = (id) => {
    return !result[id]?.parent ? 0 : getDepth(result[id].parent) + 1;
  };

  Object.values(result).forEach((_task) => {
    result[_task.id] = {
      ..._task,
      depth: getDepth(_task.id),
    };
  });

  return result;
};

export const getTemplatePhaseUpdates = (
  list: DateShiftUpdates,
  phaseList?: List,
): DateShiftUpdatesForPhase[] => {
  const updates: DateShiftUpdatesForPhase[] = [];

  Object.values(list).forEach(({ id, startDate, dueDate, dependentOn }) => {
    updates.push({
      projectPhaseId: id,
      startDate: startDate ? moment(startDate).format() : undefined,
      dueDate: dueDate ? moment(dueDate).format() : undefined,
      dependentProjectPhases:
        dependentOn != null
          ? dependentOn?.map(
              (_dep) =>
                ({
                  projectPhaseId: _dep.id,
                  projectPhaseName: phaseList ? phaseList[_dep.id].name : '',
                  dependencyType:
                    _dep.dependencyType !== undefined &&
                    _dep.dependencyType !== null
                      ? DependencyTypesEnumReverse[_dep.dependencyType]
                      : undefined,
                  lag: _dep.lag,
                } as ProjectPhase),
            )
          : [],
    });
  });

  return updates;
};

export const getTemplateTaskUpdates = (
  updateList: DateShiftUpdates,
  taskList: List,
): DateShiftUpdatesForTask[] => {
  const updates: DateShiftUpdatesForTask[] = [];

  Object.values(updateList).forEach(
    ({ id, startDate, dueDate, dependentOn }) => {
      updates.push({
        taskId: id,
        startDate: startDate ? moment(startDate).format() : undefined,
        dueDate: dueDate ? moment(dueDate).format() : undefined,
        dependentTasks: dependentOn?.map(
          (_dep) =>
            ({
              taskId: _dep.id,
              taskName: taskList[_dep.id].name,
              dependencyType:
                _dep.dependencyType !== undefined &&
                _dep.dependencyType !== null
                  ? DependencyTypesEnumReverse[_dep.dependencyType]
                  : undefined,
              lag: _dep.lag,
            } as Task),
        ),
      });
    },
  );

  return updates;
};

export const getTemplateSprintUpdates = (
  updateList: DateShiftUpdates,
): DateShiftUpdatesForSprint[] => {
  const updates: DateShiftUpdatesForSprint[] = [];

  Object.values(updateList).forEach(({ id, startDate, dueDate }) => {
    updates.push({
      sprintId: id,
      startDate: startDate ? moment(startDate).format() : undefined,
      dueDate: dueDate ? moment(dueDate).format() : undefined,
    });
  });

  return updates;
};

export const isValidDateShift = (phaseUpdatesList: DateShiftUpdates) => {
  const earliestDate: MomentInput = Object.values(phaseUpdatesList).reduce(
    (prev: MomentInput, update: EntityDateShiftUpdate): MomentInput =>
      prev === null ||
      (update.startDate && moment(update.startDate).isBefore(moment(prev)))
        ? update.startDate
        : prev,
    null,
  );

  if (earliestDate === null) {
    return true;
  }

  return moment().startOf('day').diff(moment(earliestDate), 'day', true) < 0;
};
